import { useState, useEffect, useRef, useCallback } from "react";

const API_BASE = "https://api.jeyshawnng.com" || "http://localhost:5000";

// Tracks the most-recent application ID seen so far — anything newer triggers a notification
async function fetchNewApplications(lastSeenId) {
  const res = await fetch(`${API_BASE}/api/applications`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` },
  });
  if (!res.ok) throw new Error("Failed to fetch applications");

  const { data } = await res.json(); // [{ _id, personalDetails, positionInformation, status, createdAt }]

  if (!lastSeenId) return { fresh: [], latestId: data[0]?._id ?? null };

  // data is sorted newest-first; collect everything before we hit the lastSeenId
  const fresh = [];
  for (const app of data) {
    if (app._id === lastSeenId) break;
    fresh.push(app);
  }
  return { fresh, latestId: data[0]?._id ?? lastSeenId };
}

function appToNotification(app, id) {
  const name = app.personalDetails?.fullName || "Someone";
  const role = app.positionInformation?.positionAppliedFor || "a position";
  return {
    id,
    type: "user",
    title: "New application received",
    msg: `${name} applied for ${role}`,
    time: new Date(app.createdAt).getTime(),
    read: false,
    applicationId: app._id, // useful if you want to navigate to the application on click
  };
}

export function useNotifications({ pollInterval = 30000 } = {}) {
  const [notifications, setNotifications] = useState([]);
  const [ringing, setRinging] = useState(false);
  const lastSeenId = useRef(null);   // _id of the newest application already known
  const notifId    = useRef(1);      // local incrementing id for notification items

  const ring = useCallback(() => {
    setRinging(true);
    setTimeout(() => setRinging(false), 700);
  }, []);

  const poll = useCallback(async () => {
    try {
      const { fresh, latestId } = await fetchNewApplications(lastSeenId.current);

      if (fresh.length > 0) {
        const newNotifs = fresh.map((app) => appToNotification(app, notifId.current++));
        setNotifications((prev) => [...prev, ...newNotifs]);
        ring();
      }

      // On very first poll, just record the baseline — don't flood notifications
      if (lastSeenId.current === null && latestId) {
        lastSeenId.current = latestId;
        return;
      }

      if (latestId) lastSeenId.current = latestId;
    } catch (err) {
      console.error("Notification poll failed:", err);
    }
  }, [ring]);

  useEffect(() => {
    poll(); // immediate first check
    const id = setInterval(poll, pollInterval);
    return () => clearInterval(id);
  }, [poll, pollInterval]);

  // Manually push a notification (e.g. from a WebSocket)
  const push = useCallback((notif) => {
    setNotifications((prev) => [
      ...prev,
      { ...notif, id: notifId.current++, time: Date.now(), read: false },
    ]);
    ring();
  }, [ring]);

  const markRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return { notifications, unreadCount, ringing, push, markRead, markAllRead };
}